<template>
  <div v-if="isLoggedIn">
    <h4>YOU CANNOT ACCESS THIS PAGE WHILE LOGGED IN. PLEASE LOG OUT AND RE-OPEN THE LINK TO ACCESS.</h4>
  </div>
  <div v-else class="content">
    <div class="reset-wrapper">
      <div class="reset">
        <PublicBrandHeader />
        <h3>Account Verification</h3>

        <div class="row mb-2 gx-2">
          <div class="col-12 form-group form-floating mb-2 mb-lg-0">
            <input type="text" class="form-control" :value="accountTypeChild" disabled />
            <label class="profile_label">Account Type:</label>
          </div>
        </div>

        <p class="handoff-copy">
          Accounts are no longer verified here. Your invitation e-mail comes from the
          pacmny sign-in service, and that is where you choose your username and
          password — this application never handles either.
        </p>
        <p class="handoff-copy">
          Continue to sign in below. If you cannot find your invitation, request a
          new link.
        </p>
        <p class="handoff-copy invite-expired">
          Invitations expire 30 days after they are sent. If the sign-in service
          tells you "{{ inviteExpired }}", only your organization can send you a
          new invitation; requesting a new link will not.
        </p>

        <div class="accountform-btn">
          <button type="button" class="btn btn-primary" :disabled="redirecting" @click="continueToSignIn">
            {{ redirecting ? 'Taking you there...' : 'Continue to sign in' }}
          </button>
          <a :href="forgotPasswordUrl" class="btn btn-secondary">Request a new link</a>
          <router-link to="/login" class="btn btn-secondary">Back to login</router-link>
        </div>

        <p v-if="handoffError" class="form-error-summary" role="alert">{{ handoffError }}</p>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Where an emailed "verify your account" link lands.
 *
 * The page used to finish an invitation itself: it collected a username and a
 * password, posted them to `/sessionless/updateUsername`,
 * `/sessionless/finalizeRegistration` and `/sessionless/updatePassword`, and
 * then flipped the keyon account to Active. Every one of those calls is dead —
 * the bridge intercepts `/sessionless/` and maps none of it — and none of them
 * should come back: an unauthenticated page that sets a password and activates
 * an account is an account-takeover primitive, which is exactly why the port
 * moved credentials to pacmny-auth. `POST /users/{user}/verification` is
 * deliberately server-to-server (`UserVerificationController::assertServiceClient`),
 * so there is no client-callable replacement to point at either.
 *
 * What is left is the hand-off. Signing in is the whole flow now: pacmny-auth
 * blocks the authorization request of anyone carrying `must_change_password` and
 * takes them through choosing one, so "continue to sign in" *is* "finish setting
 * up your account".
 *
 * The button starts the SPA's ordinary OIDC login rather than linking to a page
 * on the provider, so that no URL on pacmny-auth is hard-coded here — the
 * provider decides where an invitee goes, and this page only has to get them
 * there.
 */
import store from '@/store';
import oidc from '@/auth/oidcClient';
import { FORGOT_PASSWORD_URL } from '@/api/identityProvider';
import PublicBrandHeader from '@/components/public-layouts/PublicBrandHeader.vue';
import { INVITE_EXPIRED } from '@/utils/pendingAccountStatus';

export default {
  name: 'verify-account-page',
  components: {
    PublicBrandHeader,
  },
  props: {
    accountType: {
      type: String,
      default: '',
    },
    unique: {
      type: String,
      default: '',
    },
    accntnum: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      accountTypeChild: this.accountType,
      // A token-free page on the provider: it answers the same way for a known
      // and an unknown address, so linking to it says nothing about who has an
      // account here. The invitation link itself carries a token and can only be
      // minted and mailed by pacmny-auth. It is offered for an invitation that
      // cannot be found, not for an expired one: only the organization sending it
      // again revives an expired invitation (utils/pendingAccountStatus.js).
      forgotPasswordUrl: FORGOT_PASSWORD_URL,
      // Main's sentence, word for word, so the person recognises it on the
      // sign-in service's page: an invitation past its 30-day clock is revived
      // only by the organization resending it (pacmny-auth InvitationClock).
      inviteExpired: INVITE_EXPIRED,
      redirecting: false,
      handoffError: '',
    };
  },
  computed: {
    isLoggedIn() {
      return store.getters['login/isLoggedIn'];
    },
  },
  methods: {
    async continueToSignIn() {
      this.redirecting = true;
      this.handoffError = '';
      try {
        await oidc.beginLogin({ returnTo: '/welcome' });
      } catch (err) {
        // A full-page redirect that never happens leaves the button spinning
        // forever, so an unreachable provider has to be said out loud.
        this.redirecting = false;
        this.handoffError = 'The sign-in service could not be reached. Please try again in a moment.';
        console.error('Could not start sign-in:', err);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../stylesheets/mixins/alignment";

.content {
  width: 100%;
  height: auto;
  background: url("../../assets/images/bg.jpg") no-repeat center fixed;
  background-size: cover;
  position: relative;
}

.reset-wrapper {
  height: 100vh;
  width: 100%;
  position: relative;
}

.reset {
  background-color: #fff;
  padding: 2rem;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12),
    0 1px 5px 0 rgba(0, 0, 0, 0.2);
  @include absoluteCenter(760px);

  > h3 {
    text-align: center;
  }
}

.handoff-copy {
  margin: 1rem 0 0;
}

.accountform-btn {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

.form-error-summary {
  margin: 0.75rem 0 0;
  padding: 0.9rem 1.15rem;
  background: #fdecea;
  border: 1px solid #f3b4b0;
  border-radius: 6px;
  color: #9b2c2c;
}

@media only screen and (max-width: 768px) {
  .reset {
    width: 95%;
  }
}
</style>
